import React, { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { createSetupClient } from '../../api/setupClient'

interface FirstRunRedirectProps {
  children: React.ReactNode
}

/**
 * Gates the rest of the app behind a one-time check: has this tenant
 * completed admin initialization yet? Interactive deployments redirect an
 * uninitialized tenant to `/setup`. Terraform-managed deployments explicitly
 * report `setup_token_enabled=false` and retain access to `/admin` and its
 * callback so provisioned users can use the standard sign-in and first-login
 * security-onboarding flow.
 *
 * A missing capability field means an older server and fails closed to the
 * interactive wizard. Only an explicit false bypasses that redirect.
 *
 * Fails OPEN (renders children, no redirect) on any error from
 * `getInitStatus()` — a transient network hiccup or an environment where the
 * init-status endpoint is unreachable must never trap a real, already-
 * initialized deployment behind a redirect loop. This mirrors the server's
 * own InitStatus handler, which is unauthenticated by design specifically so
 * this check can run before any session exists (see init_server.go).
 */
export const FirstRunRedirect = ({
  children,
}: FirstRunRedirectProps): JSX.Element | null => {
  const [needsSetup, setNeedsSetup] = useState(false)
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    let cancelled = false
    createSetupClient()
      .getInitStatus()
      .then((resp) => {
        if (cancelled) return
        setNeedsSetup(!resp.initialized && resp.setup_token_enabled !== false)
        setChecked(true)
      })
      .catch(() => {
        if (cancelled) return
        // Fail open: treat "couldn't determine status" as "already initialized".
        setNeedsSetup(false)
        setChecked(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (!checked) return null
  if (needsSetup) return <Navigate to="/setup" replace />
  return <>{children}</>
}
